// Claude's landing engine (scroll act + WebGL crema shader + raymarched pellet),
// preserved verbatim and wrapped for React mount/unmount lifecycle.
/* eslint-disable */
export function initLanding() {
  const __cleanups = [];
  const __alive = { v: true };
  const __on = (t, ev, fn, opt) => {
    t.addEventListener(ev, fn, opt);
    __cleanups.push(() => t.removeEventListener(ev, fn, opt));
  };

(() => {
  "use strict";
  const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ── math ─────────────────────────────────────────────── */
  const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
  const seg   = (p, a, b) => clamp((p - a) / (b - a), 0, 1);
  const lerp  = (a, b, t) => a + (b - a) * t;
  const easeIO  = t => t < .5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2;
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const easeIn  = t => t * t * t;
  const bell = (p, a, b) => Math.sin(seg(p, a, b) * Math.PI);
  const win  = (p, a, b, f) => Math.min(seg(p, a, a + f), 1 - seg(p, b - f, b));

  const $  = s => document.querySelector(s);
  const $$ = s => [...document.querySelectorAll(s)];
  const mix = (a, b, t) => a.map((v, i) => Math.round(lerp(v, b[i], clamp(t, 0, 1)))).join(",");

  /* ── the pellet: an AirPods-case pebble at gum scale ──────
     Squircle footprint, flat faces, generously rounded rim,
     pure white, nothing printed on it.                        */
  /* Coated dragée pellet. Real pellet gum is a pillow: a compressed core
     built up in a pan-coating drum, so the faces bulge slightly rather than
     sitting flat, the edge is a continuous roll, and the shell is polished
     with carnauba wax — which means a soft specular sheen, not the matte
     white it had before. The coating also finishes very slightly pitted,
     so a fine speckle keeps it from reading as moulded plastic. */
  const GUM_W = 152, GUM_H = 106, GUM_D = 62, SLICES = 61;
  const PACK_D = 64;                       // pack depth — front face sits at +32
  const gum = $("#gum"), sliceEls = [];

  for (let i = 0; i < SLICES; i++) {
    const t = (i / (SLICES - 1)) * 2 - 1;
    /* high exponent => flat plateau across the faces, then a fast
       rounded fall-off at the edge. A dome curve would read as a lens. */
    /* softer exponent than a flat plateau: the faces of a panned pellet
       bow outward, they are not discs with a rim stuck on */
    const s = Math.pow(1 - 0.88 * Math.pow(Math.abs(t), 2.4), 0.40);
    const w = GUM_W * s, h = GUM_H * s;
    const lum = 0.54 + 0.46 * Math.cos((t - 0.34) * 1.35);
    /* warm off-white — a sugar shell is never pure #FFF */
    const c1 = mix([252,251,247], [178,173,165], 1 - lum);
    const c2 = mix([240,237,230], [124,120,113], 1 - lum);
    const a = Math.abs(t);
    const el = document.createElement("div");
    /* the polished treatment runs across most of the stack, not just the two
       outer discs — the pellet spins through 360° and its flank is on screen
       as much as its face */
    el.className = "slice" + (a > 0.34 ? " cap" : "") + (a > 0.93 ? " rim" : "");
    el.style.cssText =
      `width:${w}px;height:${h}px;left:${-w/2}px;top:${-h/2}px;` +
      `transform:translateZ(${t * GUM_D / 2}px);` +
      `background:linear-gradient(150deg,rgb(${c1}) 0%,rgb(${c2}) 100%);`;
    el.dataset.t = a.toFixed(4);
    gum.appendChild(el);
    sliceEls.push(el);
  }

  /* ── the three ingredients, as molecules inside the core ── */
  const core = $("#core"), molData = [];
  for (const g of [{c:"#4A2E18",n:14},{c:"#EFE3CE",n:11},{c:"#D9A441",n:9}]) {
    for (let k = 0; k < g.n; k++) {
      let x, y, z;
      do { x = Math.random()*2-1; y = Math.random()*2-1; z = Math.random()*2-1; }
      while (x*x + y*y + z*z > 1);
      const size = 3 + Math.random() * 4.5;
      const el = document.createElement("div");
      el.className = "mol";
      el.style.cssText = `width:${size}px;height:${size}px;left:${-size/2}px;top:${-size/2}px;color:${g.c};`;
      core.appendChild(el);
      molData.push({ el, x: x*GUM_W*.36, y: y*GUM_H*.34, z: z*GUM_D*.32,
                     ph: Math.random()*Math.PI*2, sp: .4+Math.random()*.9, amp: 3+Math.random()*6 });
    }
  }

  /* ── refs ─────────────────────────────────────────────── */
  const wrap = $(".scroll-wrap"), stage = $("#stage"), rig = $("#rig");
  const packet = $("#packet"), lid = $("#lid"), nav = $("#nav"), cue = $("#cue");
  const rail = $("#rail"), railBtns = $$("#rail button");
  const pools = [$("#poolA"), $("#poolB"), $("#poolC")];
  const beans = $$(".bean");
  const copies = ["k1","k2","k3","k5"].map(id => $("#" + id));
  const heroMark = copies[0].querySelector("h1");

  /* ingredient blocks — the big type */
  const ings = ["ing1","ing2","ing3"].map(id => {
    const el = $("#" + id);
    return { el, name: el.querySelector(".ing-name"), dose: el.querySelector(".ing-dose"), chars: [] };
  });
  const ING_BANDS = [[.615,.715,.028],[.705,.805,.028],[.795,.895,.028]];

  const COPY_BANDS = [
    [-.50,  .140, .050],   // k1 hero
    [ .155, .315, .045],   // k2
    [ .450, .600, .045],   // k3
    [ .915, 1.40, .045]    // k5 CTA
  ];

  /* ── per-character split, so headlines animate letter by letter ── */
  let charsSplit = false;
  function splitChars() {
    ings.forEach(o => {
      const txt = o.name.textContent;
      o.name.textContent = "";
      o.chars = [...txt].map(c => {
        const s = document.createElement("span");
        s.className = "ch";
        s.textContent = c === " " ? " " : c;
        o.name.appendChild(s);
        return s;
      });
    });
    charsSplit = true;
  }
  function unsplitChars() {
    ings.forEach(o => {
      o.name.textContent = o.name.textContent.replace(/ /g, " ");
      o.chars = [];
      o.name.style.transform = "";
    });
    charsSplit = false;
  }

  /* ── viewport ─────────────────────────────────────────── */
  let vw = 0, vh = 0, U = 1, isNarrow = false, docMax = 1;
  function measure() {
    vw = innerWidth;
    vh = stage.clientHeight || innerHeight;
    isNarrow = vw < 860;
    U = clamp(Math.min(vw / 1440, vh / 900), 0.42, 1.14);
    docMax = Math.max(1, wrap.offsetHeight - vh);
  }

  /* ── scroll ───────────────────────────────────────────── */
  let target = 0, smooth = 0, intro = 0;
  function readScroll() {
    const y = window.scrollY || document.documentElement.scrollTop;
    target = clamp((y - wrap.offsetTop) / docMax, 0, 1);
    nav.classList.toggle("solid", y > 40);
    rail.classList.toggle("on",
      y > vh * .35 && y < wrap.offsetTop + wrap.offsetHeight - vh * .4);
  }

  /* ── render ───────────────────────────────────────────── */
  let clock = 0;
  function render(p, dt) {
    clock += dt;

    /* CAMERA */
    const dolly =
      lerp(0, -120, easeIO(seg(p, .10, .42))) +
      lerp(0, -300, easeIO(seg(p, .42, .70))) +
      lerp(0,  260, easeIO(seg(p, .82, 1)));
    rig.style.transform =
      `translateZ(${dolly.toFixed(2)}px) ` +
      `rotateX(${lerp(6, 0, easeIO(seg(p, .02, .30))).toFixed(2)}deg) ` +
      `rotateY(${(lerp(-16, 0, easeIO(seg(p, .02, .30))) + Math.sin(clock*.22)*1.6*(1-seg(p,.55,.75))).toFixed(2)}deg)`;

    /* PACK — flies in on load, turns to face, lid flips, then retreats */
    const pIn   = easeOut(intro);
    const pTurn = easeIO(seg(p, .08, .28));
    const pOut  = easeIn(seg(p, .34, .50));

    const pkY = lerp(120, 0, pIn) + Math.sin(clock*.6)*7*(1-pTurn*.5) + lerp(0, 90, pOut);
    const pkZ = lerp(-300, 0, pIn) + lerp(0, -700, pOut);
    packet.style.transform =
      `translate3d(0,${pkY.toFixed(2)}px,${pkZ.toFixed(2)}px) ` +
      /* settles at -10°: tipped toward the camera so the open mouth reads */
      `rotateX(${(lerp(14,-10,pTurn) + Math.cos(clock*.4)*1.1*(1-pTurn*.7)).toFixed(2)}deg) ` +
      `rotateY(${(lerp(-34,4,pTurn) + Math.sin(clock*.5)*1.4*(1-pTurn*.7)).toFixed(2)}deg) ` +
      `rotateZ(${lerp(-9,0,pTurn).toFixed(2)}deg) ` +
      `scale(${(U * lerp(.86,1,pIn) * lerp(1,.82,pOut)).toFixed(4)})`;
    packet.style.opacity = Math.min(pIn, 1 - easeIn(seg(p, .38, .50))).toFixed(3);

    /* the lid hinges backward off the top-rear edge */
    lid.style.transform =
      /* hinge = top-rear edge of the body: y -127 (body top), z -32 (half depth).
         These must track the shell geometry in CSS — when they drifted apart
         the lid swung about a point that was not on the box. */
      `translate3d(0,-127px,-32px) rotateX(${lerp(0, 104, easeIO(seg(p, .24, .36))).toFixed(2)}deg)`;

    /* PELLET */
    const rise   = easeIO(seg(p, .26, .38));
    const travel = easeIO(seg(p, .38, .54));
    const mag    = easeIO(seg(p, .56, .74));
    const settle = easeIO(seg(p, .90, 1));

    const spinT = seg(p, .34, .82);
    /* lifts a little during the reveal so the single line of type
       sits clear beneath it */
    const gY = lerp(60, -215, rise) + lerp(0, 197, travel)
             + Math.sin(clock*.7)*6*(1-mag)*travel
             + (isNarrow ? -vh*.13 : -vh*.06) * mag
             + lerp(0, -70, settle);
    const gZ = lerp(-150, 190, travel) + lerp(0, 210, mag) + lerp(0, -260, settle);
    const gX = Math.sin(seg(p, .38, .66) * Math.PI) * (isNarrow ? 26 : 54) * (1 - mag);

    const gRZ = Math.sin(spinT*Math.PI*2)*14*(1-mag);
    const gRY = easeIO(seg(p,.34,.84))*360 + Math.sin(clock*.35)*4*(1-mag);
    const gRX = easeOut(spinT)*1080;
    /* pellet is ~19mm wide against a 55mm pack, so it reads as something
       that would actually drop out of that opening */
    const gS  = U * (.52 + .48*travel) * lerp(1, isNarrow ? 2.5 : 3.1, mag) * lerp(1,.46,settle);
    /* The shader canvas paints above the pack, so the pellet cannot be hidden
       by the box — it has to be faded in only once it has actually cleared
       the lip. At p .325 it is still inside; by .345 it is above the mouth,
       with the lid ~98° open. Fading across that window reads as the piece
       rising out rather than materialising through the lid. */
    const gOp = seg(p, .325, .350);

    gum.style.transform =
      `translate3d(${gX.toFixed(2)}px,${gY.toFixed(2)}px,${gZ.toFixed(2)}px) ` +
      `rotateZ(${gRZ.toFixed(2)}deg) rotateY(${gRY.toFixed(2)}deg) rotateX(${gRX.toFixed(2)}deg) ` +
      `scale(${gS.toFixed(4)})`;
    gum.style.opacity = gOp.toFixed(3);

    /* same numbers, handed to the shader — nothing about the timing,
       easing or camera is recomputed here */
    if (window.__pellet) {
      const persp = 1250 / Math.max(1, 1250 - gZ);   // match the CSS perspective
      /* The shader samples ro.xy = uv * 2.6/scl, and uv spans ±0.5 of the
         viewport height, so the pellet covers a fraction f of the height when
         scl = f * 2.6/(2*0.55). The old pellet was 152px wide at scale 1,
         aspect 1.73, so 88px tall — hence f = 88*gS*persp/vh. DPR cancels,
         because both uv and pos are normalised by the same device height. */
      window.__pellet({
        x: gX * persp, y: gY * persp,
        s: 208 * gS * persp / Math.max(1, vh),
        rx: gRX, ry: gRY, rz: gRZ, opa: gOp, t: clock
      });
    }

    /* X-RAY */
    /* The pellet stays a solid, opaque, coated object for the whole act.
       The old x-ray dropped it to 4% opacity with molecules floating inside
       for a third of the scroll — whatever else it was, it was not a
       photograph of a piece of gum. */
    const xray = 0;
    if (render._xray !== 0) {
      render._xray = 0;
      for (const el of sliceEls) el.style.opacity = "1";
      core.style.opacity = "0";
    }
    if (xray > .01) {
      for (const m of molData) {
        const w = clock * m.sp + m.ph;
        m.el.style.transform =
          `translate3d(${(m.x + Math.sin(w)*m.amp).toFixed(2)}px,` +
          `${(m.y + Math.cos(w*.8)*m.amp).toFixed(2)}px,` +
          `${(m.z + Math.sin(w*1.3)*m.amp*.6).toFixed(2)}px)`;
      }
    }

    /* INGREDIENTS — one line at a time, letters popping up in sequence */
    ings.forEach((o, i) => {
      const [a, b, f] = ING_BANDS[i];
      const op = win(p, a, b, f);
      o.el.style.opacity = op.toFixed(3);
      if (op <= 0) return;

      const e = easeOut(seg(p, a, a + f + .03));
      o.el.style.transform = `translateY(${lerp(18, 0, e).toFixed(1)}px)`;

      if (charsSplit) {
        const n = o.chars.length;
        for (let k = 0; k < n; k++) {
          const ce = easeOut(clamp((e * (n + 7) - k) / 7, 0, 1));
          o.chars[k].style.transform = `translateY(${lerp(56, 0, ce).toFixed(1)}px)`;
          o.chars[k].style.opacity = ce.toFixed(3);
        }
      }
      const em = easeOut(seg(p, a + f * .5, a + f + .06));
      o.dose.style.opacity = em.toFixed(3);
      o.dose.style.transform = `translateY(${lerp(16, 0, em).toFixed(1)}px)`;
    });

    /* COPY */
    copies.forEach((el, i) => {
      const [a, b, f] = COPY_BANDS[i];
      const o = win(p, a, b, f), r = 1 - o;
      el.style.opacity = o.toFixed(3);
      el.style.transform =
        `translate(-50%,${el.classList.contains("pin-bottom") ? "0%" : "-50%"}) ` +
        `translateY(${(r*30).toFixed(1)}px) scale(${lerp(1,.975,r).toFixed(4)})`;
      el.style.pointerEvents = o > .25 ? "auto" : "none";
      // a fully faded layer must not sit on top of what is underneath it
      el.style.visibility = o < .02 ? "hidden" : "visible";
    });
    heroMark.style.letterSpacing = (.02 + seg(p, 0, .16) * .06) + "em";

    /* ATMOSPHERE */
    const warm = bell(p, .30, .90);
    pools[0].style.transform = `translate3d(${(-40+p*90).toFixed(1)}px,${(60-p*190).toFixed(1)}px,0) scale(${(1+p*.5).toFixed(3)})`;
    pools[0].style.opacity = (.55 + warm*.35).toFixed(3);
    pools[1].style.transform = `translate3d(${(180-p*320).toFixed(1)}px,${(-120+p*210).toFixed(1)}px,0) scale(${(.8+warm*.8).toFixed(3)})`;
    pools[1].style.opacity = (.25 + warm*.6).toFixed(3);
    pools[2].style.transform = `translate3d(${(-200+p*260).toFixed(1)}px,${(150-p*300).toFixed(1)}px,0)`;
    pools[2].style.opacity = (.3 + bell(p,.55,1)*.5).toFixed(3);

    beans.forEach((b, i) => {
      const op = +b.dataset.op * (1 - seg(p, .55, .8));
      if (op <= 0) { if (b.style.opacity !== "0") b.style.opacity = "0"; return; }
      const d = +b.dataset.depth, dirn = i % 2 ? 1 : -1;
      b.style.transform =
        `translate3d(${(dirn*p*190*d).toFixed(1)}px,${(-p*460*d + Math.sin(clock*.5+i)*9).toFixed(1)}px,0) ` +
        `perspective(620px) ` +
        `rotateX(${(Math.sin(clock*.38+i*1.7)*16).toFixed(1)}deg) ` +
        `rotateY(${(Math.cos(clock*.31+i*2.3)*20).toFixed(1)}deg) ` +
        `rotate(${(+b.dataset.rot + p*250*d*dirn).toFixed(1)}deg)`;
      b.style.opacity = op.toFixed(3);
    });

    cue.style.opacity = (1 - seg(p, .01, .07)).toFixed(3);

    const marks = [0, .26, .38, .61, .915];
    let act = 0;
    for (let i = 0; i < marks.length; i++) if (p >= marks[i]) act = i;
    if (act !== render._act) {
      render._act = act;
      railBtns.forEach((b, i) => b.classList.toggle("now", i === act));
    }
  }
  render._xray = -1; render._act = -1;

  /* ── rAF ──────────────────────────────────────────────── */
  let last = performance.now(), running = true;
  function frame(now) {
    const dt = Math.min(.05, (now - last) / 1000);
    last = now;
    intro = Math.min(1, intro + dt / 1.4);
    smooth += (target - smooth) * (1 - Math.pow(.0016, dt));
    if (Math.abs(target - smooth) < 3e-5) smooth = target;
    render(smooth, dt);
    if (running && __alive.v) requestAnimationFrame(frame);
  }

  /* ── boot ─────────────────────────────────────────────── */
  /* any line left empty (or holding only a stray <br>) is hidden, so
     deleted copy does not leave a gap in the layout */
  function collapseBlanks() {
    $$(".copy .eyebrow,.copy .lede,.copy h1,.copy h2,.sec h3,.sec .sub,.kicker," +
       ".stat span,.step .m,.step .t,.step p,.flav h4,.flav .note,.flav p,.flav .mg," +
       ".buy .per,.buy .fine,.blurb,.foot-base span,.foot-grid h5," +
       ".pk-side-line,.pk-caff,.pk-chew,.pk-claim,.pk-flavsub,.pk-barnum,.ing-dose")
      .forEach(el => el.classList.toggle("is-blank", !el.textContent.trim()));
  }

  function boot() {
    collapseBlanks();
    measure();
    readScroll();
    smooth = target;
    intro = target > .02 ? 1 : 0;

    if (reduce) {
      intro = 1;
      render(.66, 0);
      copies[0].style.cssText += ";opacity:1;position:static;transform:none;margin-bottom:8vh";
      copies[3].style.opacity = "1";
      ings.forEach(o => { o.dose.style.opacity = "1"; o.dose.style.transform = "none"; });
      return;
    }
    splitChars();
    requestAnimationFrame(frame);
  }

  __on(window, "scroll", readScroll, { passive: true });
  __on(window, "resize", () => { measure(); readScroll(); }, { passive: true });
  __on(window, "orientationchange", () => setTimeout(() => { measure(); readScroll(); }, 220));

  railBtns.forEach(b => b.addEventListener("click", () =>
    scrollTo({ top: wrap.offsetTop + docMax * parseFloat(b.dataset.p), behavior: "smooth" })));

  $$('a[href^="#"]').forEach(a => a.addEventListener("click", e => {
    const t = document.querySelector(a.getAttribute("href"));
    if (!t) return;
    e.preventDefault();
    scrollTo({ top: t === wrap ? 0 : t.offsetTop - 60, behavior: "smooth" });
  }));

  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(en => en.forEach(x => {
      if (x.isIntersecting) { x.target.classList.add("in"); io.unobserve(x.target); }
    }), { rootMargin: "0px 0px -12% 0px", threshold: .15 });
    __cleanups.push(() => io.disconnect());
    $$(".rise").forEach((el, i) => { el.style.transitionDelay = (i % 5) * 70 + "ms"; io.observe(el); });
  } else $$(".rise").forEach(el => el.classList.add("in"));

  __on(document, "visibilitychange", () => {
    if (document.hidden) running = false;
    else if (!reduce) { running = true; last = performance.now(); requestAnimationFrame(frame); }
  });

  boot();
})();

/* Background shader — WebGL1 for Safari compatibility.
   Domain-warped fBm: noise whose sample position is itself displaced by
   noise, which is what gives the slow folding motion of crema rather than
   the plasma look of raw fBm. Everything is deliberately low-contrast.
   Bails out silently if WebGL is unavailable, leaving the flat background. */
(function(){
  var cv = document.getElementById("bgfx");
  if (!cv) return;
  var gl = cv.getContext("webgl", {alpha:true, antialias:false, depth:false,
                                   premultipliedAlpha:false, powerPreference:"low-power"})
        || cv.getContext("experimental-webgl");
  if (!gl) return;

  var VS = "attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}";

  var FS = [
  "precision mediump float;",
  "uniform vec2 res;uniform float t;",
  "float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}",
  "float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);",
  "  return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}",
  "float fbm(vec2 p){float v=0.,a=.5;for(int k=0;k<5;k++){v+=a*n(p);p*=2.02;a*=.5;}return v;}",
  "void main(){",
  "  vec2 uv=(gl_FragCoord.xy-.5*res)/res.y;",
  "  float tt=t*.016;",
  /* warp the sample position with two offset noise fields */
  "  vec2 q=vec2(fbm(uv*1.5+vec2(0.,tt)),fbm(uv*1.5+vec2(5.2,1.3-tt)));",
  "  vec2 r=vec2(fbm(uv*1.5+4.*q+vec2(1.7,9.2)+tt*.7),fbm(uv*1.5+4.*q+vec2(8.3,2.8)-tt*.5));",
  "  float f=fbm(uv*1.5+4.*r);",
  /* espresso -> bean -> roast, with a thin gold only at the very top end */
  "  vec3 c=mix(vec3(.078,.051,.035),vec3(.141,.094,.067),clamp(f*1.5,0.,1.));",
  "  c=mix(c,vec3(.227,.153,.106),clamp(pow(f,2.2)*1.35,0.,1.));",
  "  c=mix(c,vec3(.541,.400,.216),clamp(pow(f,7.)*.9,0.,1.));",
  /* settle the corners so the centre of the stage stays readable */
  "  float vig=1.-.72*dot(uv*vec2(.62,1.),uv*vec2(.62,1.));",
  "  c*=clamp(vig,0.,1.);",
  "  gl_FragColor=vec4(c,1.);",
  "}"].join("\n");

  function sh(type, src){
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.warn(gl.getShaderInfoLog(s)); return null; }
    return s;
  }
  var vs = sh(gl.VERTEX_SHADER, VS), fs = sh(gl.FRAGMENT_SHADER, FS);
  if (!vs || !fs) return;
  var prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
  gl.useProgram(prog);

  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,3,-1,-1,3]), gl.STATIC_DRAW);
  var loc = gl.getAttribLocation(prog, "p");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  var uRes = gl.getUniformLocation(prog, "res");
  var uT   = gl.getUniformLocation(prog, "t");

  /* half resolution, capped — this is soft noise, nobody can tell, and it
     keeps mid-range phones from cooking */
  function size(){
    var d = Math.min(window.devicePixelRatio || 1, innerWidth < 860 ? 1.1 : 1.5) * 0.5;
    var w = Math.max(1, Math.floor(innerWidth * d));
    var hh = Math.max(1, Math.floor(innerHeight * d));
    if (cv.width !== w || cv.height !== hh){ cv.width = w; cv.height = hh; }
    gl.viewport(0, 0, cv.width, cv.height);
    gl.uniform2f(uRes, cv.width, cv.height);
  }
  __on(window, "resize", size, {passive:true});
  size();

  var reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var running = !reduce, t0 = performance.now();

  function draw(now){
    gl.uniform1f(uT, (now - t0) / 1000);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    if (running && __alive.v) requestAnimationFrame(draw);
  }
  __on(document, "visibilitychange", function(){
    if (document.hidden) running = false;
    else if (!reduce){ running = true; t0 = performance.now() - 1e4; requestAnimationFrame(draw); }
  });

  requestAnimationFrame(function(n){ draw(n); cv.classList.add("on"); });
})();

/* ── PELLET ──────────────────────────────────────────────────────────────
   The old pellet was 61 flat divs stacked along Z. That construction can
   never look seamless: every layer boundary is a real step in the surface,
   which is the contour banding and the rectangular tiling.

   This replaces it with a signed-distance field raymarched in a fragment
   shader. The surface is analytic — evaluated per pixel, never tessellated
   — so there is no subdivision to show through at any zoom, and the bevels
   are exact rather than approximated by stacking.

   Shape: a rounded box, wider than tall and thin front-to-back, with heavy
   corner rounding. That is a pillow pellet, not a rectangle.

   Motion is not reinvented. The existing render() already computes the
   pellet's position, spin and scale; those values are handed over as
   uniforms each frame, so timing, easing and camera are untouched.       */
(function(){
  var cv = document.getElementById("pellet");
  if (!cv) return;
  var gl = cv.getContext("webgl", {alpha:true, antialias:false, depth:false, premultipliedAlpha:true})
        || cv.getContext("experimental-webgl");
  if (!gl){ var g = document.getElementById("gum"); if (g) g.style.visibility = "visible"; return; }

  var VS = "attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}";
  var FS = [
"precision highp float;",
"uniform vec2 res;uniform vec3 pos;uniform float scl,opa,tm;uniform mat3 rot;",

/* A plain rounded box cannot make a thin pillow: its bevel radius is capped
   by the smallest half-extent, so a thin piece always stays boxy. Instead:
   a rounded-rectangle footprint, extruded, with the extrusion edge rolled
   by its own radius. Corner roundness and thickness become independent —
   which is exactly how a pressed gum pellet is shaped. */
"float sdPillow(vec3 p,vec2 b,float cr,float hz,float er){",
" vec2 q=abs(p.xy)-b+cr;",
" float d2=length(max(q,0.))+min(max(q.x,q.y),0.)-cr;",
" float dz=abs(p.z)-hz;",
" vec2 w=vec2(d2+er,dz+er);",
" return min(max(d2,dz),0.)+length(max(w,0.))-er;}",

"float hash(vec3 p){return fract(sin(dot(p,vec3(127.1,311.7,74.7)))*43758.5453);}",
"float noise(vec3 p){vec3 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);",
" return mix(mix(mix(hash(i),hash(i+vec3(1,0,0)),f.x),mix(hash(i+vec3(0,1,0)),hash(i+vec3(1,1,0)),f.x),f.y),",
"            mix(mix(hash(i+vec3(0,0,1)),hash(i+vec3(1,0,1)),f.x),mix(hash(i+vec3(0,1,1)),hash(i+vec3(1,1,1)),f.x),f.y),f.z);}",

/* 19 x 11 x 5 mm at pellet scale. Corner radius 0.50 against a 0.55 half-
   height gives a fill ratio of 0.89 — measured against the silhouette, that
   is the pillow band: rounder and it turns oval, squarer and it reads as a
   tablet. */
"float map(vec3 p){return sdPillow(rot*p,vec2(0.95,0.55),0.50,0.26,0.16);}",

"vec3 nrm(vec3 p){vec2 e=vec2(0.0012,0.);",
" return normalize(vec3(map(p+e.xyy)-map(p-e.xyy),map(p+e.yxy)-map(p-e.yxy),map(p+e.yyx)-map(p-e.yyx)));}",

"void main(){",
" vec2 uv=(gl_FragCoord.xy-.5*res)/res.y;",
" vec3 ro=vec3((uv-pos.xy/res.y)*2.6/max(scl,.0001),2.6);",
" vec3 rd=vec3(0.,0.,-1.);",                     /* orthographic: matches the CSS scene's flat feel */
" float t=0.,d=0.;bool hit=false;",
" for(int i=0;i<64;i++){vec3 p=ro+rd*t;d=map(p);if(d<.0009){hit=true;break;}t+=d;if(t>6.)break;}",
" if(!hit){gl_FragColor=vec4(0.);return;}",
" vec3 p=ro+rd*t, n=nrm(p);",

/* micro texture: fine noise perturbing the normal, so highlights break up
   the way they do on a panned sugar shell rather than staying glassy */
" float mn=noise(rot*p*46.)-.5;",
" n=normalize(n+vec3(mn)*0.055);",

" vec3 L1=normalize(vec3(-.45,.78,.62));",       /* key, upper-left */
" vec3 L2=normalize(vec3(.62,-.30,.42));",       /* bounce, lower-right */
" vec3 V=vec3(0.,0.,1.);",
" float dif=max(dot(n,L1),0.), bnc=max(dot(n,L2),0.);",
" float spe=pow(max(dot(reflect(-L1,n),V),0.),34.)*0.30;",
" float fre=pow(1.-max(dot(n,V),0.),3.5)*0.22;",

/* white, but never blown out — a real pellet sits around 0.93, not 1.0 */
" vec3 base=vec3(0.955,0.951,0.941);",
" vec3 c=base*(0.60+0.34*dif+0.16*bnc)+vec3(spe)+vec3(fre)*vec3(1.,.99,.96);",
/* faint grain so it reads as manufactured rather than rendered */
" c+= (noise(rot*p*150.)-.5)*0.020;",
" c=min(c,vec3(1.));",
" float a=opa*smoothstep(.0016,.0006,d);",
" gl_FragColor=vec4(c*a,a);",
"}"].join("\n");

  function sh(t,s){var o=gl.createShader(t);gl.shaderSource(o,s);gl.compileShader(o);
    if(!gl.getShaderParameter(o,gl.COMPILE_STATUS)){console.warn(gl.getShaderInfoLog(o));return null;}return o;}
  var vs=sh(gl.VERTEX_SHADER,VS), fs=sh(gl.FRAGMENT_SHADER,FS);
  if(!vs||!fs){var g2=document.getElementById("gum");if(g2)g2.style.visibility="visible";return;}
  var pr=gl.createProgram();gl.attachShader(pr,vs);gl.attachShader(pr,fs);gl.linkProgram(pr);
  if(!gl.getProgramParameter(pr,gl.LINK_STATUS)){var g3=document.getElementById("gum");if(g3)g3.style.visibility="visible";return;}
  gl.useProgram(pr);
  gl.enable(gl.BLEND); gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  var bf=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,bf);
  gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
  var al=gl.getAttribLocation(pr,"p");gl.enableVertexAttribArray(al);
  gl.vertexAttribPointer(al,2,gl.FLOAT,false,0,0);

  var U={res:gl.getUniformLocation(pr,"res"),pos:gl.getUniformLocation(pr,"pos"),
         scl:gl.getUniformLocation(pr,"scl"),opa:gl.getUniformLocation(pr,"opa"),
         tm:gl.getUniformLocation(pr,"tm"),rot:gl.getUniformLocation(pr,"rot")};

  function size(){
    var dpr=Math.min(window.devicePixelRatio||1, innerWidth<860?1.5:2);
    var w=Math.max(1,Math.round(cv.clientWidth*dpr)), hh=Math.max(1,Math.round(cv.clientHeight*dpr));
    if(cv.width!==w||cv.height!==hh){cv.width=w;cv.height=hh;}
    gl.viewport(0,0,cv.width,cv.height);
    gl.uniform2f(U.res,cv.width,cv.height);
  }
  __on(window, "resize", size, {passive:true});

  function rad(d){return d*Math.PI/180;}
  /* column-major 3x3 for the inverse rotation (object <- world) */
  function setRot(rx,ry,rz){
    var cx=Math.cos(rx),sx=Math.sin(rx),cy=Math.cos(ry),sy=Math.sin(ry),cz=Math.cos(rz),sz=Math.sin(rz);
    var m=[ cy*cz, cy*sz, -sy,
            sx*sy*cz-cx*sz, sx*sy*sz+cx*cz, sx*cy,
            cx*sy*cz+sx*sz, cx*sy*sz-sx*cz, cx*cy ];
    gl.uniformMatrix3fv(U.rot,false,new Float32Array(m));
  }

  window.__pellet = function(st){
    if(!cv.clientHeight) return;
    size();
    gl.clearColor(0,0,0,0); gl.clear(gl.COLOR_BUFFER_BIT);
    if(st.opa<=0.002) return;
    var dpr=cv.width/Math.max(1,cv.clientWidth);
    gl.uniform3f(U.pos, st.x*dpr, -st.y*dpr, 0.);
    gl.uniform1f(U.scl, st.s);          /* already viewport-relative; DPR cancels */
    gl.uniform1f(U.opa, st.opa);
    gl.uniform1f(U.tm, st.t);
    setRot(rad(st.rx),rad(st.ry),rad(st.rz));
    gl.drawArrays(gl.TRIANGLES,0,3);
  };
  size();
})();

  __cleanups.push(() => { window.__pellet = null; });
  return () => { __alive.v = false; __cleanups.forEach((fn) => { try { fn(); } catch (e) {} }); };
}
